import {
  ArrowLeftIcon,
  DownloadSimpleIcon,
  FileTextIcon,
  MapPinIcon,
  PencilSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import useSWR from 'swr';

import { PageHeader } from 'components/PageHeader';
import { Pill } from 'components/pill/Pill';
import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { TextArea } from 'components/textarea/TextArea';
import { Input } from 'components/input/Input';
import { ToggleGroup } from 'components/toggle-group/ToggleGroup';
import { invariant } from '@utils/invariant';
import { formatDateTime } from '@utils/date';
import { getDisplayError } from '@utils/get-display-error';
import { Alert } from 'components/alert/Alert';
import { MultiFileUploaderButton } from 'src/app/document/components/MultiFileUploaderButton';
import { FileUploaderButton } from 'src/app/document/components/FileUploaderButton';
import { ConfirmDialog } from 'components/dialog/ConfirmDialog';
import { useAuth } from 'contexts/auth-context';
import { InternalRole } from 'src/app/auth/endpoints/MeEndpoint.types';
import { roleToNumber } from 'src/app/auth/roles.client';
import { constructDownloadUrl } from 'src/app/document/models';
import { TrailerCheckPhotoGallery } from '../components/TrailerCheckPhotoGallery';
import {
  fetchTrailerCheck,
  addTrailerCheckPhotos,
  updateTrailerCheck,
  type TrailerCheckEventTypeType,
} from '../models';
import { useTrailer } from './trailerContext';
import { useTrailerCheckDetailUi } from './useTrailerCheckDetailUi';
import { formatDate } from '@utils/date';
import type { Document } from 'src/types/enums';
import { ErrorState } from 'components/error-state/ErrorState';
import { SpinnerBlock } from 'components/Spinner';
import type { TrailerCheckDetailResponse, TrailerCheckPhotoResponse } from '../endpoints/TrailerCheckEndpoint.types';

const DAMAGE_CATEGORY_LABELS: Record<string, string> = {
  Tires: 'Banden',
  Brakes: 'Remmen',
  Lights: 'Verlichting',
  Body: 'Carrosserie',
  Floor: 'Vloer',
  Tarpaulin: 'Zeil',
  Doors: 'Deuren',
  Coupling: 'Koppeling',
  Other: 'Overig',
};

const EVENT_TYPE_LABELS: Record<TrailerCheckEventTypeType, string> = {
  PickedUp: 'Opgehaald',
  DroppedOff: 'Afgezet',
};

function getTrailerCheckViewerPhotos(check: TrailerCheckDetailResponse): TrailerCheckPhotoResponse[] {
  const photosById = new Map<string, TrailerCheckPhotoResponse>();

  for (const photo of check.photos) {
    photosById.set(photo.id, photo);
  }

  for (const damage of check.damages) {
    for (const photo of damage.photos) {
      photosById.set(photo.id, photo);
    }
  }

  return Array.from(photosById.values());
}

export const TrailerCheckDetailPage = () => {
  const { trailerId, checkId } = useParams<{ trailerId: string; checkId: string }>();
  invariant(trailerId);
  invariant(checkId);

  const { me } = useAuth();
  const { trailer } = useTrailer();
  const canResolve = roleToNumber(me.role) >= roleToNumber(InternalRole.InternalEmployeePlus);

  const { data: check, error, mutate } = useSWR(['trailer-check', checkId], () => fetchTrailerCheck(checkId));
  const { resolution, notes, details, forms, actions } = useTrailerCheckDetailUi(checkId, mutate);

  if (!check) {
    return error ? (
      <ErrorState error={error} onRetry={() => mutate()} variant="section" />
    ) : (
      <SpinnerBlock message="Trailer check laden..." />
    );
  }
  const statusColor = check.cannotContinue ? 'negative' : check.status === 'Damaged' ? 'primary' : 'positive';
  const statusLabel = check.status === 'OK' ? 'OK' : 'Beschadigd';
  const generalPhotos = check.photos.filter((p) => !p.damageId);
  const viewerPhotos = getTrailerCheckViewerPhotos(check);

  return (
    <>
      <PageHeader title={`Trailer check - ${check.trailerLicensePlate}`} />

      <div className="px-4 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/internal/trailers/${trailerId}/checks`}>
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <span className="text-fg-muted">
              {EVENT_TYPE_LABELS[check.eventType]} &middot; {formatDate(check.eventDate)}
            </span>
          </div>
          <div className="flex gap-2">
            {check.photos.length > 0 ? (
              <a
                href={`/api/v1/internal/trailer/trailer-check/${checkId}/photos/download`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button color="secondary" iconLeft={<DownloadSimpleIcon className="button-icon" />}>
                  Download foto's
                </Button>
              </a>
            ) : (
              <Button color="secondary" iconLeft={<DownloadSimpleIcon className="button-icon" />} isDisabled>
                Geen foto's
              </Button>
            )}
            <a
              href={`/api/v1/internal/trailer/trailer-check/${checkId}/export/pdf`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button color="secondary" iconLeft={<FileTextIcon className="button-icon" />}>
                Export PDF
              </Button>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <div className="label-text">Type</div>
            <div>{EVENT_TYPE_LABELS[check.eventType]}</div>
          </div>
          <div>
            <div className="label-text">Datum</div>
            <div>{formatDate(check.eventDate)}</div>
          </div>
          <div>
            <div className="label-text">Vervoerder</div>
            {check.supplierId ? (
              <Link to={`/internal/suppliers/${check.supplierId}/general`} className="link-text">
                {check.supplierName}
              </Link>
            ) : (
              <div className="text-fg-muted">-</div>
            )}
          </div>
          <div>
            <div className="label-text">Chauffeur</div>
            <div>{check.driverName || <span className="text-fg-muted">-</span>}</div>
          </div>
          <div>
            <div className="label-text">Order</div>
            {check.orderId ? (
              <div className="flex items-center gap-2">
                <Link to={`/internal/orders/${check.orderId}/general`} className="link-text">
                  {check.orderNumber || check.orderId}
                </Link>
                <ConfirmDialog
                  triggerColor="danger"
                  triggerText={<XIcon className="button-icon" />}
                  title="Order loskoppelen?"
                  submitText="Loskoppelen"
                  description="Wil je de gekoppelde order verwijderen uit deze trailer check?"
                  onSubmit={async () => {
                    try {
                      await updateTrailerCheck(checkId, { orderId: null });
                      toast.success('Order losgekoppeld');
                      mutate();
                    } catch (err) {
                      toast.error('Mislukt: ' + getDisplayError(err));
                    }
                  }}
                />
              </div>
            ) : (
              <div className="text-fg-muted">-</div>
            )}
          </div>
          <div>
            <div className="label-text">Status</div>
            <Pill color={statusColor} size="sm">
              {statusLabel}
            </Pill>
          </div>
          <div>
            <div className="label-text">Doorrijden</div>
            {check.cannotContinue ? (
              <Pill color="negative" size="sm">
                Niet mogelijk
              </Pill>
            ) : (
              <span>Mogelijk</span>
            )}
          </div>
          <div>
            <div className="label-text">Actief</div>
            <div>{check.isActive ? 'Ja' : 'Nee'}</div>
          </div>
          <div>
            <div className="label-text">Aangemaakt in portaal</div>
            <div className="text-fg-muted">{formatDateTime(check.createdAt)}</div>
          </div>
          {trailer.isRental && (
            <>
              <div>
                <div className="label-text">Datum start huur</div>
                <div>
                  {check.rentalStartDate ? formatDate(check.rentalStartDate) : <span className="text-fg-muted">-</span>}
                </div>
              </div>
              <div>
                <div className="label-text">Datum einde huur</div>
                <div>
                  {check.rentalEndDate ? formatDate(check.rentalEndDate) : <span className="text-fg-muted">-</span>}
                </div>
              </div>
              <div>
                <div className="label-text mb-1">Factuur verhuurmaatschappij</div>
                {check.invoiceDocument && (
                  <a
                    href={constructDownloadUrl({
                      documentId: check.invoiceDocument.documentId,
                      documentHash: check.invoiceDocument.documentHash,
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-text block mb-1"
                  >
                    {check.invoiceDocument.documentName}
                  </a>
                )}
                <FileUploaderButton
                  buttonText={check.invoiceDocument ? 'Factuur vervangen' : 'Factuur uploaden'}
                  title="Factuur verhuurmaatschappij"
                  onSubmit={async (doc) => {
                    await updateTrailerCheck(checkId, { invoiceDocumentId: doc.id });
                    mutate();
                  }}
                  unlinkDocument={
                    check.invoiceDocument
                      ? async () => {
                          await updateTrailerCheck(checkId, { invoiceDocumentId: null });
                          mutate();
                        }
                      : undefined
                  }
                  file={
                    check.invoiceDocument
                      ? {
                          id: check.invoiceDocument.documentId,
                          name: check.invoiceDocument.documentName,
                          hash: check.invoiceDocument.documentHash,
                        }
                      : null
                  }
                />
              </div>
            </>
          )}
        </div>

        {check.cannotContinue && (
          <Alert type="error" title="Doorrijden niet mogelijk">
            Deze trailer kan niet verder rijden.
          </Alert>
        )}

        <Card title="Check gegevens">
          {details.editing ? (
            <div className="space-y-4">
              <div>
                <div className="label-text mb-2">Status</div>
                <ToggleGroup
                  options={[
                    { value: 'OK', label: 'OK' },
                    { value: 'Damaged', label: 'Beschadigd' },
                  ]}
                  value={details.draft.status}
                  onChange={(status) => forms.patchDetailsDraft({ status })}
                />
              </div>
              <div>
                <div className="label-text mb-2">Doorrijden</div>
                <ToggleGroup
                  options={[
                    { value: 'true', label: 'Niet mogelijk' },
                    { value: 'false', label: 'Mogelijk' },
                  ]}
                  value={details.draft.cannotContinue ? 'true' : 'false'}
                  onChange={(value) => forms.patchDetailsDraft({ cannotContinue: value === 'true' })}
                />
              </div>
              <div>
                <div className="label-text mb-2">Type</div>
                <ToggleGroup
                  options={[
                    { value: 'PickedUp', label: 'Opgehaald' },
                    { value: 'DroppedOff', label: 'Afgezet' },
                  ]}
                  value={details.draft.eventType}
                  onChange={(eventType) => forms.patchDetailsDraft({ eventType })}
                />
              </div>
              <label className="block">
                <div className="label-text mb-1">Datum</div>
                <Input
                  type="date"
                  value={details.draft.eventDate}
                  onChange={(eventDate) => forms.patchDetailsDraft({ eventDate })}
                />
              </label>
              <div>
                <div className="label-text mb-2">Actief</div>
                <ToggleGroup
                  options={[
                    { value: 'true', label: 'Ja' },
                    { value: 'false', label: 'Nee' },
                  ]}
                  value={details.draft.isActive ? 'true' : 'false'}
                  onChange={(value) => forms.patchDetailsDraft({ isActive: value === 'true' })}
                />
              </div>
              <label className="block">
                <div className="label-text mb-1">Locatie</div>
                <Input
                  type="text"
                  value={details.draft.locationName}
                  onChange={(locationName) => forms.patchDetailsDraft({ locationName })}
                />
              </label>
              {trailer.isRental && (
                <>
                  <label className="block">
                    <div className="label-text mb-1">Datum start huur</div>
                    <Input
                      type="date"
                      value={details.draft.rentalStartDate}
                      onChange={(rentalStartDate) => forms.patchDetailsDraft({ rentalStartDate })}
                    />
                  </label>
                  <label className="block">
                    <div className="label-text mb-1">Datum einde huur</div>
                    <Input
                      type="date"
                      value={details.draft.rentalEndDate}
                      onChange={(rentalEndDate) => forms.patchDetailsDraft({ rentalEndDate })}
                    />
                  </label>
                </>
              )}
              <label className="block">
                <div className="label-text mb-1">Algemene opmerkingen</div>
                <TextArea
                  rows={3}
                  value={details.draft.generalRemarks}
                  onChange={(generalRemarks) => forms.patchDetailsDraft({ generalRemarks })}
                />
              </label>
              <div className="flex gap-2">
                <Button color="primary" onTrigger={actions.handleSaveDetails} isLoading={details.isSaving}>
                  Opslaan
                </Button>
                <Button color="secondary" onTrigger={forms.cancelDetailsEdit} isDisabled={details.isSaving}>
                  Annuleren
                </Button>
              </div>
            </div>
          ) : (
            <div>
              {(check.locationName || (check.latitude && check.longitude)) && (
                <div className="mb-4">
                  <div className="label-text">Locatie</div>
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="w-4 h-4" />
                    {check.locationName && <span>{check.locationName}</span>}
                    {check.latitude && check.longitude && (
                      <a
                        href={`https://www.google.com/maps?q=${check.latitude},${check.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-text"
                      >
                        Bekijk op kaart
                      </a>
                    )}
                  </div>
                </div>
              )}
              <div className="mb-4">
                <div className="label-text">Algemene opmerkingen</div>
                <div className="whitespace-pre-wrap">
                  {check.generalRemarks || <span className="text-fg-muted">-</span>}
                </div>
              </div>
              <Button
                color="secondary"
                onTrigger={() => forms.startDetailsEdit(check)}
                iconLeft={<PencilSimpleIcon className="button-icon" />}
              >
                Bewerken
              </Button>
            </div>
          )}
        </Card>

        <div>
          <div className="label-text mb-2">Algemene foto's</div>
          {generalPhotos.length > 0 && (
            <div className="mb-2">
              <TrailerCheckPhotoGallery photos={generalPhotos} viewerPhotos={viewerPhotos} />
            </div>
          )}
          <MultiFileUploaderButton
            buttonText="Foto's toevoegen"
            title="Algemene foto's toevoegen"
            files={[]}
            uploadAsImages
            onSubmit={async (documents: Document[], close) => {
              try {
                await addTrailerCheckPhotos(checkId, {
                  documentIds: documents.map((d) => d.id),
                });
                toast.success("Foto's toegevoegd");
                mutate();
                close();
              } catch (err) {
                toast.error('Mislukt: ' + getDisplayError(err));
              }
            }}
            unlinkDocument={async () => {}}
          />
        </div>

        {check.damages.length > 0 && (
          <Card title="Schades">
            <div className="space-y-4">
              {check.damages.map((damage) => (
                <div key={damage.id}>
                  <div className="font-medium text-fg">
                    {DAMAGE_CATEGORY_LABELS[damage.category] ?? damage.category}
                  </div>
                  {damage.remarks && <div className="text-fg-muted whitespace-pre-wrap mb-2">{damage.remarks}</div>}
                  {damage.photos.length > 0 && (
                    <TrailerCheckPhotoGallery photos={damage.photos} viewerPhotos={viewerPhotos} />
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card title="Oplossing">
          {check.resolved ? (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Pill color="positive" size="sm">
                  Opgelost
                </Pill>
                <span className="text-fg-muted">
                  door {check.resolvedByName} op {formatDateTime(check.resolvedAt!)}
                </span>
              </div>
              {resolution.editing ? (
                <div>
                  <div className="mb-2">
                    <TextArea
                      rows={3}
                      value={resolution.editedNote}
                      onChange={forms.setEditedResolutionNote}
                      placeholder="Beschrijf de oplossing..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button color="primary" onTrigger={actions.handleSaveResolution} isLoading={resolution.isSaving}>
                      Opslaan
                    </Button>
                    <Button color="secondary" onTrigger={forms.cancelResolutionEdit} isDisabled={resolution.isSaving}>
                      Annuleren
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  {check.resolutionNote && <div className="whitespace-pre-wrap mb-2">{check.resolutionNote}</div>}
                  {canResolve && (
                    <Button
                      color="secondary"
                      onTrigger={() => forms.startResolutionEdit(check.resolutionNote ?? '')}
                      iconLeft={<PencilSimpleIcon className="button-icon" />}
                    >
                      Bewerken
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : check.status === 'Damaged' && canResolve ? (
            <div>
              <div className="mb-2">
                <label>
                  <div className="label-text">Opmerking bij oplossing</div>
                  <TextArea
                    rows={3}
                    value={resolution.note}
                    onChange={forms.setResolutionNote}
                    placeholder="Beschrijf de oplossing..."
                  />
                </label>
              </div>
              <Button color="primary" onTrigger={actions.handleResolve} isLoading={resolution.isResolving}>
                Markeer als opgelost
              </Button>
            </div>
          ) : check.status === 'Damaged' ? (
            <div className="text-fg-muted">Niet opgelost</div>
          ) : (
            <div className="text-fg-muted">Geen schade - geen oplossing nodig</div>
          )}
        </Card>

        <Card title="Interne notities">
          {check.notes.length > 0 && (
            <div className="mb-4">
              {check.notes.map((note, index) => (
                <div key={note.id} className={`py-3 ${index < check.notes.length - 1 ? 'border-b border-border' : ''}`}>
                  <div className="flex items-center gap-2 text-sm text-fg-muted mb-1">
                    <span className="font-medium text-fg">{note.authorName}</span>
                    <span>&middot;</span>
                    <span>{formatDateTime(note.createdAt)}</span>
                    {notes.editingId !== note.id && (
                      <button
                        type="button"
                        className="rounded text-fg-muted hover:bg-surface-hover hover:text-fg"
                        onClick={() => forms.startNoteEdit(note.id, note.content)}
                      >
                        <PencilSimpleIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {notes.editingId === note.id ? (
                    <div>
                      <div className="mb-2">
                        <TextArea rows={2} value={notes.editedContent} onChange={forms.setEditedNoteContent} />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          color="primary"
                          onTrigger={() => actions.handleSaveNote(note.id)}
                          isLoading={notes.isSaving}
                          isDisabled={!notes.editedContent.trim()}
                        >
                          Opslaan
                        </Button>
                        <Button color="secondary" onTrigger={forms.cancelNoteEdit} isDisabled={notes.isSaving}>
                          Annuleren
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap">{note.content}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <TextArea
                rows={2}
                value={notes.newContent}
                onChange={forms.setNotesNewContent}
                placeholder="Voeg een interne notitie toe..."
              />
            </div>
            <Button
              color="secondary"
              onTrigger={actions.handleAddNote}
              isLoading={notes.isAdding}
              isDisabled={!notes.newContent.trim()}
            >
              Toevoegen
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
};
